import {
  Component,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { Subscription, Subject, takeUntil } from 'rxjs';
import { TabsService } from '../services/tabs.service';
import { TabView } from '../shared/primeng-compat/legacy-tabview.component';
import { RoleAuthorizationService } from '../../../services/role-authorization.service';
import { MsalService } from '@azure/msal-angular';
import { AzureLoginService } from '../../../services/azure-login.service';
import { ApolloService } from '../../../services/apollo.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DropdownDataService } from '../services/dropdown.data.service';
import { CrmDocumentType } from '../shared/constants/enums';
import { UserIdleService } from 'angular-user-idle';
import moment from 'moment';
import { StringService } from '../../../services/string.service';
import { TOKEN_REFRESH_TIME, TIMEOUT_COUNT } from 'src/app/app-config';
import { ErrorHandlerService } from '../services/error-handler.service';
import { FeatureFlagsService } from '../services/feature-flags.service';
import { ApiConfigService } from '../services/api-config.service';
import {
  copy,
  sanitizeDeepLinkQueryParamKey,
  sanitizeDeepLinkQueryParamValue,
} from '../shared/utilities/common-utilities';
import { ScmModuleMapping } from './module-mapping';
import {
  IVR_SEARCH_HANDOFF_CHANNEL_NAME,
  IVR_SEARCH_HANDOFF_MESSAGE_TYPE,
} from './ivr-search-handoff.constants';
import { Location } from '@angular/common';
import { SecurityTypeService } from '../administration/security/security.service';

@Component({
  standalone: false,
  selector: 'crm-layout',
  templateUrl: 'crm-layout.template.html',
  styleUrls: ['crm-layout.scss'],
})
export class CrmLayoutComponent implements OnDestroy, OnInit {
  tabs: any[] = [];
  changeTab: any[] = [];
  subscription: Subscription;
  display: boolean;
  selectedIndex = 0;
  isDirty: boolean;
  closingIndex: number;
  isLoading = true;
  querySubscription: Subscription;
  documentType = CrmDocumentType;
  errorDisplay = false;
  errorMessage = '';
  erMessage = '';
  tokenCreationDate;
  tokenExpireDate;
  isTimingOutFlag = false;
  isDirectAccess = false;
  isDirectAccessModule: any;

  public strings = null;
  private unsubscribe: Subject<void> = new Subject();
  private ivrHandoffChannel: BroadcastChannel | null = null;
  private pendingIvrHandoffPayloads: Record<string, unknown>[] = [];
  countdownValue = 0;

  public activityCtx = {
    sessionTimeoutStamp: new Date(),
    sessionTimeoutCallback: null,
    sessionTimingOut: false,
    sessionTimingOutCount: 0,
    sessionTimingOutCountText: '0:00',
    sessionTimingOutStamp: null,
    sessionRefreshTokenNeeded: false,
  };

  @ViewChild(TabView) tabView: TabView;

  constructor(
    private tabService: TabsService,
    private cdr: ChangeDetectorRef,
    private roleService: RoleAuthorizationService,
    private msalService: MsalService,
    private azureService: AzureLoginService,
    private apollo: ApolloService,
    private router: Router,
    private dropdownDataService: DropdownDataService,
    private userIdle: UserIdleService,
    private stringService: StringService,
    private errorHandlerService: ErrorHandlerService,
    private featureFlagService: FeatureFlagsService,
    private apiConfigService: ApiConfigService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private configurationService: SecurityTypeService,
  ) {
    this.stringService.strings$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((strings) => (this.strings = strings));

    this.subscription = this.tabService.getTab().subscribe((tab) => {
      if (this.tabs.length && this.tabView && this.tabView.tabs) {
        this.clearTabSelection();
      }
      if (this.isDirectAccess) {
        this.tabs = [];
      }
      tab.selected = true;
      this.tabs.push(tab);
      this.selectedIndex = this.tabs.length - 1;
    });
    this.display = false;
  }

  @HostListener('click') onclick() {
    this.restart();
  }

  ngOnInit() {
    this.activatedRoute.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe((params) => {
      const payload = this.parseDeepLinkParams(params);
      if (payload) {
        this.isDirectAccess = true;
        console.log(payload);
        this.isDirectAccessModule = payload;
      }
    });

    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.ivrHandoffChannel = new BroadcastChannel(IVR_SEARCH_HANDOFF_CHANNEL_NAME);
        this.ivrHandoffChannel.onmessage = (event: MessageEvent) => {
          const data = event.data;
          if (
            !data ||
            data.type !== IVR_SEARCH_HANDOFF_MESSAGE_TYPE ||
            !data.params ||
            typeof data.params !== 'object'
          ) {
            return;
          }
          const payload = this.parseDeepLinkParams(data.params as Params);
          if (payload) {
            this.pendingIvrHandoffPayloads.push(payload);
            this.tryFlushIvrHandoffQueue();
          }
        };
      } catch {
        this.ivrHandoffChannel = null;
      }
    }
    /* Start of User Idle Logic */
    this.tokenCreationDate = moment();
    this.tokenExpireDate = moment();
    this.tokenExpireDate.add(TOKEN_REFRESH_TIME, 'seconds');
    // This is the idle logic.

    // Start watching for user inactivity.
    this.userIdle.startWatching();

    // Start watching when user idle is starting.
    this.userIdle.onTimerStart().subscribe((count) => {
      if (count && !this.isTimingOutFlag) this.isTimingOutFlag = true;
      if (TIMEOUT_COUNT >= count) {
        this.activityCtx.sessionTimingOutCountText = (TIMEOUT_COUNT - count).toString();
      }
    });

    // Start watch when time is up.
    this.userIdle.onTimeout().subscribe(() => {
      console.log('User Idle Timeout Hit. Logging off...');
      this.logOut();
    });

    // Do check periodically
    this.userIdle.ping$.subscribe(() => {
      const currentDate = moment();
      this.azureService.getAccessToken();
      if (this.tokenExpireDate < moment()) {
        console.log('Token Expiring. Renewing...');
        this.azureService.refreshAccessToken();
        this.tokenCreationDate = moment();
        this.tokenExpireDate = moment();
        this.tokenExpireDate.add(TOKEN_REFRESH_TIME, 'seconds');
      }
    });
    /* End of User Idle Logic */

    let userRole = 'SSD-Med';
    let userStatus = '';
    this.azureService.asyncTokenLookup().then((data: string | null) => {
      if (!data) {
        this.logOut();
        return;
      }
      this.azureService.setAccessToken(data);
      this.azureService.syncAccountFromMsal();
      this.azureService.resetRolePermissions();
      this.roleService.PermissionsList = {};
      this.roleService.Permissions = {};
      this.roleService.Operations = {};
      this.apollo
        .createCRM()
        .then(() => {
          const accountDetails = this.msalService.instance.getActiveAccount();
          const azureUserId = accountDetails?.localAccountId ?? this.azureService.getAccountIdValue();
          if (!azureUserId) {
            this.logOut();
            return;
          }
          this.querySubscription = this.roleService.getUserDetails(azureUserId).subscribe(
            ({ data }) => {
              this.azureService.workQueueLink = [];
              if (data.getAdminSettings.length > 0) {
                userRole = data.getAdminSettings[0].attachedRole;
                userStatus = data.getAdminSettings[0].status;
                this.roleService.userRole = userRole;
                this.azureService.roleName = userRole;
                this.azureService.firstName = data.getAdminSettings[0].firstName;
                this.azureService.lastName = data.getAdminSettings[0].lastName;
                this.azureService.fullName = `${data.getAdminSettings[0].firstName} ${data.getAdminSettings[0].lastName}`;
                this.setUserLinks(data.getAdminSettings[0]);
              }
              this.querySubscription = this.roleService.getOperationDetails().subscribe(
                // Disable the Rule Shadowed name: 'data'
                /* tslint:disable */
                ({ data }) => {
                  /* tslint:enable */
                  for (const singleOperation of data.getAdminDisplayPermissions) {
                    const operationId = parseInt(singleOperation.operationId, 10);
                    const operationName = singleOperation.operationName;
                    this.roleService.Operations[operationName] = operationId;
                  }
                  this.querySubscription = this.roleService.getPermissionListDetails('%').subscribe(
                    // Disable the Rule Shadowed name: 'data'
                    /* tslint:disable */
                    ({ data }) => {
                      /* tslint:enable */
                      for (const singlePermission of data.getAdminDisplayPermissions) {
                        const component = singlePermission.componentName;
                        const elementName = singlePermission.elementName;
                        const listName = singlePermission.listName;
                        const parentComponent = singlePermission.parentComponentName;
                        if (!(component in this.roleService.PermissionsList)) {
                          this.roleService.PermissionsList[component] = {};
                        }
                        if (parentComponent) {
                          if (!(parentComponent in this.roleService.PermissionsList[component])) {
                            this.roleService.PermissionsList[component][parentComponent] = {};
                          }
                          this.roleService.PermissionsList[component][parentComponent][
                            elementName
                          ] = true;
                        } else {
                          if (listName && listName !== '') {
                            if (!(elementName in this.roleService.PermissionsList[component])) {
                              this.roleService.PermissionsList[component][elementName] = {};
                            }
                            if (
                              !(
                                'Options' in
                                this.roleService.PermissionsList[component][elementName]
                              )
                            ) {
                              this.roleService.PermissionsList[component][elementName].Options = {};
                            }
                            this.roleService.PermissionsList[component][elementName].Options[
                              listName
                            ] = true;
                          } else {
                            this.roleService.PermissionsList[component][elementName] = true;
                          }
                        }
                      }
                      this.querySubscription = this.roleService.getRoleDetails(userRole).subscribe(
                        // Disable the Rule Shadowed name: 'data'
                        /* tslint:disable */
                        ({ data }) => {
                          /* tslint:enable */
                          for (const singlePermission of data.getAdminDisplayPermissions) {
                            const component = singlePermission.componentName;
                            const elementName = singlePermission.elementName;
                            const listName = singlePermission.listName;
                            const operationName = singlePermission.operationName;
                            const parentComponent = singlePermission.parentComponentName;
                            const roleName = singlePermission.roleName;
                            if (roleName !== userRole) {
                              continue;
                            } // Filter out wild card extra rows.
                            if (!(userRole in this.roleService.Permissions)) {
                              this.roleService.Permissions[userRole] = {};
                            }
                            if (!(component in this.roleService.Permissions[userRole])) {
                              this.roleService.Permissions[userRole][component] = {};
                            }
                            if (parentComponent) {
                              if (
                                !(
                                  parentComponent in
                                  this.roleService.Permissions[userRole][component]
                                )
                              ) {
                                this.roleService.Permissions[userRole][component][parentComponent] =
                                  {};
                              }
                              if (
                                !(
                                  elementName in
                                  this.roleService.Permissions[userRole][component][parentComponent]
                                )
                              ) {
                                this.roleService.Permissions[userRole][component][parentComponent][
                                  elementName
                                ] = {};
                              }
                              const operationValue = this.roleService.Operations[operationName];
                              for (const singleOperationName in this.roleService.Operations) {
                                if (
                                  this.roleService.Operations.hasOwnProperty(singleOperationName)
                                ) {
                                  const singleOperationId =
                                    this.roleService.Operations[singleOperationName];
                                  if (operationValue >= singleOperationId) {
                                    if (
                                      !(
                                        'Operation' in
                                        this.roleService.Permissions[userRole][component][
                                          parentComponent
                                        ][elementName]
                                      )
                                    ) {
                                      this.roleService.Permissions[userRole][component][
                                        parentComponent
                                      ][elementName].Operation = {};
                                    }
                                    this.roleService.Permissions[userRole][component][
                                      parentComponent
                                    ][elementName].Operation[singleOperationName] = true;
                                  }
                                }
                              }
                            } else {
                              if (
                                !(elementName in this.roleService.Permissions[userRole][component])
                              ) {
                                this.roleService.Permissions[userRole][component][elementName] = {};
                              }
                              if (listName && listName !== '') {
                                if (
                                  !(
                                    'Options' in
                                    this.roleService.Permissions[userRole][component][elementName]
                                  )
                                ) {
                                  this.roleService.Permissions[userRole][component][
                                    elementName
                                  ].Options = {};
                                }
                                this.roleService.Permissions[userRole][component][
                                  elementName
                                ].Options[listName] = true;
                              } else {
                                const operationValue = this.roleService.Operations[operationName];
                                for (const singleOperationName in this.roleService.Operations) {
                                  if (
                                    this.roleService.Operations.hasOwnProperty(singleOperationName)
                                  ) {
                                    const singleOperationId =
                                      this.roleService.Operations[singleOperationName];
                                    if (operationValue >= singleOperationId) {
                                      if (
                                        !(
                                          'Operation' in
                                          this.roleService.Permissions[userRole][component][
                                            elementName
                                          ]
                                        )
                                      ) {
                                        this.roleService.Permissions[userRole][component][
                                          elementName
                                        ].Operation = {};
                                      }
                                      this.roleService.Permissions[userRole][component][
                                        elementName
                                      ].Operation[singleOperationName] = true;
                                    }
                                  }
                                }
                              }
                            }
                            // this.roleService.Permissions[userRole][component][elementName] = { Operation: { View: true } };
                          }
                          if (userStatus.toLowerCase() === 'inactive') {
                            window.alert(
                              'User login has been disabled.  Please contact CSGHelpDesk@newleafsc.net for assistance.',
                            );
                            this.router.navigate(['']);
                            this.msalService.logoutRedirect();
                            return;
                          }
                          this.loadRolePermissionSearch(userRole, userStatus);
                        },
                        (err) => {
                          userRole = 'Customer';
                          this.isLoading = false;
                          window.alert('Role Retrieval Failed. Please try again in a few minutes.');
                          this.router.navigate(['']);
                        },
                      );
                    },
                    (err) => {
                      userRole = 'Customer';
                      this.isLoading = false;
                      window.alert(
                        'Permission Retrieval Failed. Please try again in a few minutes.',
                      );
                      this.router.navigate(['']);
                    },
                  );
                },
                (err) => {
                  userRole = 'Customer';
                  this.isLoading = false;
                  window.alert('Operation Retrieval Failed. Please try again in a few minutes.');
                  this.router.navigate(['']);
                },
              );
            },
            (err) => {
              userRole = 'Customer';
              this.isLoading = false;
              window.alert('User Retrieval Failed. Please try again in a few minutes.');
              this.router.navigate(['']);
            },
          );
          this.dropdownDataService.getDropdownData().subscribe(({ data }) => {
            this.dropdownDataService.dealerGroupData = [...data.getDealerGroupCache].sort((a,b)=> a.dealerGroupName.localeCompare(b.dealerGroupName));   
            this.dropdownDataService.buyingGroupData = [...data.getBuyingGroupCache].sort((a,b)=> a.name.localeCompare(b.name));
            this.dropdownDataService.masterBuyingGroupData = [...data.getMasterBuyingGroupCache].sort((a,b)=> a.name.localeCompare(b.name));
            this.dropdownDataService.userListData = data.getUserListCache;
            this.dropdownDataService.servicerListData = [...data.getServicerListCache].sort((a,b)=> a.key.localeCompare(b.key));
            this.dropdownDataService.servicerGroupList = data.getServicerGroupListCache;
            this.dropdownDataService.categoryDescriptionList = data.getCategoryDescriptionList;
            this.dropdownDataService.categoryList = data.getCategoryList;
            this.dropdownDataService.productPlanTypeList = data.getAllProductPlanTypesList;
            this.dropdownDataService.subCategoryDescriptionList =
              data.getAllSubCategoryCodeAndDescription;
            this.dropdownDataService.userProfileTypesList = data.getUserProfileTypes;
            this.dropdownDataService.servicerListLinkData = data.getServicerListLink;
            this.dropdownDataService.roleNameData = data.getRoleNameCache;
            this.dropdownDataService.menuTypeList = data.getMenuTypeList;
            this.dropdownDataService.stateList = data.getStateList;
            this.dropdownDataService.claimStatusData = data.getClaimStatusCache;
            this.dropdownDataService.tierDescriptionData = data.getTierDescriptionList;
            this.dropdownDataService.operationsList = data.getOperationsList;
            this.dropdownDataService.permissionsList = [];
            this.dropdownDataService.permissionDescription = [];
            this.dropdownDataService.operationsListOriginal = data.getPermissionsList;
            this.dropdownDataService.dealerLocationList = data.getAllDealerLocations;
            this.dropdownDataService.workQueueListTypes = data.getWorkQueueListTypes || [];
            this.dropdownDataService.transactionYear = data.getTransactionYear;
            data.getPermissionsList.forEach((perm) => {
              const tmp = perm.value.split('-');
              const permissionsListIndex = this.dropdownDataService.permissionsList.findIndex(
                (e) => e.value === tmp[0],
              );
              if (permissionsListIndex === -1) {
                this.dropdownDataService.permissionsList.push({
                  key: perm.key,
                  value: tmp[0],
                });
              }
              const dropIndex = this.dropdownDataService.permissionDescription.findIndex(
                (e) => e.value === tmp[1],
              );
              if (dropIndex === -1) {
                this.dropdownDataService.permissionDescription.push({
                  key: perm.key,
                  value: tmp[1],
                });
              }
            });
            this.dropdownDataService.manufacturerDetailList = data.getManufacturerByName;
            this.dropdownDataService.moduleTypeList = [...data.getAllModuleTypes].sort((a,b)=> a.value.localeCompare(b.value));

            if (data.getManufacturerByName) {
              this.dropdownDataService.manufacturerList = [];
              data.getManufacturerByName.forEach((mfl) => {
                this.dropdownDataService.manufacturerList.push({
                  label: mfl.manufacturersName,
                  value: mfl.manufacturersName,
                });
              });

              this.dropdownDataService.manufacturerList.push({ label: 'ALL', value: 'ALL' });
            }
          });

          this.featureFlagService.getAllEnabledFeatures().subscribe(({ data }) => {
            const activeApis = copy(data.getActiveApis);
            if (activeApis && activeApis.length > 0) {
              this.featureFlagService.addEnabledFeatures(activeApis.map((el) => el.apiName));
              activeApis.forEach((el) => {
                this.apiConfigService.getApiConfigDetails(el.apiName).subscribe(
                  // tslint:disable-next-line:no-shadowed-variable
                  ({ data }) => {
                    const apiConfigs = copy(data.getApiConfigs);
                    if (apiConfigs) {
                      this.apiConfigService.addApiConfig(el.apiName, apiConfigs);
                    } else {
                      this.apiConfigService.addApiConfig(el.apiName, null);
                    }
                  },
                  (err) => {
                    this.apiConfigService.addApiConfig(el.apiName, null);
                  },
                );
              });
            }
          });
        })
        .catch(() => {
          console.log(`Error on graphql`);
        });

      this.errorHandlerService.errorNotification.subscribe((err) => {
        this.errorDisplay = true;
        this.errorMessage = err;
        this.erMessage = err;
      });
    });
  }

  /**
   * Normalizes URL / BroadcastChannel query payloads for Search Contract / Service Order deep links.
   */
  private parseDeepLinkParams(raw: Params | Record<string, unknown>): Record<string, unknown> | null {
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    const request: Record<string, string> = {};
    Object.keys(raw).forEach((key) => {
      try {
        const fkey = sanitizeDeepLinkQueryParamKey(key);
        if (!fkey) {
          return;
        }
        const rawVal = raw[key];
        const valStr = Array.isArray(rawVal) ? rawVal[0] : rawVal;
        if (valStr === undefined || valStr === null) {
          return;
        }
        const value = sanitizeDeepLinkQueryParamValue(String(valStr));
        if (value !== 'N/A' && value !== '') {
          request[fkey] = value;
        }
      } catch (error: unknown) {
        console.log((error as Error).message, key);
      }
    });
    if (Object.keys(request).length === 0) {
      return null;
    }
    console.log('request', JSON.stringify(request));
    return Object.assign({ isQuerySearch: true }, request);
  }

  private openDirectAccessTabFromPayload(modulePayload: Record<string, unknown>): void {
    const mod = modulePayload.module as string;
    const tInfo = ScmModuleMapping[mod];
    console.log('tInfo', JSON.stringify(tInfo));
    if (!tInfo) {
      return;
    }
    this.tabService.createTab({
      header: tInfo.header,
      content: modulePayload,
      type: tInfo.type,
    });
  }

  private tryFlushIvrHandoffQueue(): void {
    if (this.isLoading || this.pendingIvrHandoffPayloads.length === 0) {
      return;
    }
    while (this.pendingIvrHandoffPayloads.length > 0) {
      const p = this.pendingIvrHandoffPayloads.shift();
      if (p) {
        this.openDirectAccessTabFromPayload(p);
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.ivrHandoffChannel) {
      this.ivrHandoffChannel.close();
      this.ivrHandoffChannel = null;
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  handleClose(e) {
    this.closingIndex = e.index;
    if (this.tabs[e.index].isDirty) {
      this.display = true;
    } else {
      this.display = false;
      this.clearTabSelection();
      this.setTabHighlight();
      this.tabs.splice(e.index, 1);
      e.close();
      this.cdr.detectChanges();
    }
  }

  handleChange(e) {
    this.selectedIndex = e.index;
    this.clearTabSelection();
    if (this.tabs[e.index]) {
      this.tabs[e.index].selected = true;
    }
    if (this.tabView?.tabs?.[e.index]) {
      this.tabView.tabs[e.index].selected = true;
    }
  }

  onYes() {
    this.display = false;
    this.clearTabSelection();
    this.setTabHighlight();
    this.display = false;
    this.tabs.splice(this.closingIndex, 1);
    this.cdr.detectChanges();
  }

  // Function to identify if form component is dirty.
  setChangeData(isDirty: boolean) {
    this.tabs[this.selectedIndex].isDirty = isDirty;
  }

  setTabHighlight() {
    if (this.selectedIndex === this.closingIndex) {
      if (this.selectedIndex > 0) {
        this.selectedIndex = this.selectedIndex - 1;
      } else {
        this.selectedIndex = 0;
      }
      if (this.tabs[this.selectedIndex]) {
        this.tabs[this.selectedIndex].selected = true;
      }
      if (this.tabView?.tabs?.[this.selectedIndex]) {
        this.tabView.tabs[this.selectedIndex].selected = true;
      }
    } else if (this.selectedIndex > this.closingIndex) {
      // Mark current selection before splice; index shifts down after close.
      if (this.tabs[this.selectedIndex]) {
        this.tabs[this.selectedIndex].selected = true;
      }
      if (this.tabView?.tabs?.[this.selectedIndex]) {
        this.tabView.tabs[this.selectedIndex].selected = true;
      }
      this.selectedIndex = this.selectedIndex - 1;
    } else {
      if (this.tabs[this.selectedIndex]) {
        this.tabs[this.selectedIndex].selected = true;
      }
      if (this.tabView?.tabs?.[this.selectedIndex]) {
        this.tabView.tabs[this.selectedIndex].selected = true;
      }
    }
  }

  clearTabSelection() {
    for (const tab of this.tabs) {
      if (tab.selected) {
        tab.selected = false;
      }
    }
    if (this.tabView && this.tabView.tabs) {
      for (const tab of this.tabView.tabs) {
        if (tab.selected) {
          tab.selected = false;
        }
      }
    }
  }

  stop() {
    this.userIdle.stopTimer();
  }

  stopWatching() {
    this.userIdle.stopWatching();
  }

  startWatching() {
    this.userIdle.startWatching();
  }

  restart() {
    this.userIdle.resetTimer();
    this.isTimingOutFlag = false;
  }

  isTimingOut() {
    return this.isTimingOutFlag;
  }

  logOut() {
    this.msalService.logoutRedirect().subscribe(() => {
      // Use window.location.href to ensure it works in all environments (including dev with baseHref)
      window.location.href = '/';
    });
  }

  /**
   * Loads SEC role-permission rows after legacy PermissionsList/Permissions are ready so the
   * sidebar and directives evaluate both permission systems together.
   */
  private loadRolePermissionSearch(userRole: string, userStatus: string): void {
    this.configurationService
      .getRolePermissionSearch({ roleName: userRole, limit: 100000 })
      .subscribe({
        next: (rolePermissionsQuery) => {
          this.azureService.setRolePermissions(
            rolePermissionsQuery.data?.getRolePermissionSearch ?? [],
          );
          this.completeLoginBootstrap(userStatus);
        },
        error: (err) => {
          console.error('Role permission search failed', err);
          this.azureService.setRolePermissions([]);
          this.completeLoginBootstrap(userStatus);
        },
      });
  }

  private completeLoginBootstrap(userStatus: string): void {
    this.isLoading = false;

    if (this.isDirectAccess) {
      this.isDirectAccess = false;
      this.openDirectAccessTabFromPayload(this.isDirectAccessModule);
    }
    this.tryFlushIvrHandoffQueue();
  }

  setUserLinks(userDetails) {
    if (!userDetails.userProfileType) {
      return;
    }
    const userProfileTypes = userDetails.userProfileType.split('||');
    const userProfileNumber = userDetails.userProfileNumber.split('||');
    for (let index = 0; index < userProfileTypes.length; index++) {
      if (userProfileTypes[index] === 'Servicer') {
        this.azureService.servicerNumberLink.push(userProfileNumber[index]);
      }
      if (userProfileTypes[index] === 'Dealer') {
        this.azureService.dealerNumberLink.push(userProfileNumber[index]);
      }
      if (userProfileTypes[index] === 'MyWorkQueue') {
        this.azureService.workQueueLink.push(userProfileNumber[index]);
      }
    }
  }
}
